import moment from "moment";
import { bloodGroups, maritalStatus, sectionWiseFields } from "../constants/people";
import {
  createPresignedPost,
  hrReplaceDocument,
} from "../store/OnboardingSlice/thunk";
import {
  EDUCATION_QUALIFICATION_LEVELS,
  getEducationDegreeLabel,
  normalizeEducationQualification,
} from "./educationHelpers";
import {
  extractOnboardingActionData,
  extractRequestVersion,
  rejectMessageFromThunk,
} from "./onboardingHelpers";

/**
 * Backend document_type values for create_presigned_post / hr_replace_document.
 * Only maps docs that are already HR-replaceable in Review Request.
 */
const DOC_ID_TO_DOCUMENT_TYPE = {
  "doc-resignation": "RESIGNATION_LETTER",
  "doc-relieving": "RELIEVING_LETTER",
  "doc-epfo": "EPFO_PASSBOOK",
  "doc-form2": "PF_FORM2",
  "doc-form11": "PF_FORM11",
  "doc-pf-authorization": "PF_AUTHORIZATION",
  "doc-formf": "FORM_F",
  "doc-nischint": "NISCHINT_FORM",
};

/**
 * Resolve backend document_type for an HR-replaceable UI doc id.
 */
export const resolveHrReplaceDocumentType = (docId) => {
  const id = String(docId || "").trim();
  if (!id) return "";

  if (DOC_ID_TO_DOCUMENT_TYPE[id]) return DOC_ID_TO_DOCUMENT_TYPE[id];

  if (/^doc-edu-marksheet-\d+$/.test(id)) return "CONSOLIDATED_MARKSHEET";
  if (/^doc-edu-certificate-\d+$/.test(id)) return "GRADUATION_CERTIFICATE";

  return "";
};

/**
 * Education documents are one card per record: doc-edu-marksheet-1 is
 * record_index 1. Other sections have no repeating document record.
 */
const parseEducationDocumentRecordIndex = (docId = "") => {
  const match = String(docId || "").match(
    /^doc-edu-(?:marksheet|certificate)-(\d+)$/,
  );
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const readFiniteRecordIndex = (value) => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * record_index for hr_replace_document. Explicit index wins, then the
 * document card, then the education document id. 0 is a valid index.
 */
export const resolveHrDocumentRecordIndex = (params = {}) => {
  const doc = params.doc && typeof params.doc === "object" ? params.doc : {};
  const explicit = readFiniteRecordIndex(
    params.recordIndex != null
      ? params.recordIndex
      : params.record_index != null
        ? params.record_index
        : doc.recordIndex != null
          ? doc.recordIndex
          : doc.record_index,
  );
  if (explicit != null) return explicit;

  return parseEducationDocumentRecordIndex(
    params.docId || params.fieldKey || doc.id || "",
  );
};

export const readDocRefId = (doc = {}) => {
  if (!doc || typeof doc !== "object") return "";
  const value =
    doc.doc_ref_id ?? doc.docRefId ?? doc.doc_ref ?? doc.ref_id ?? "";
  return value == null ? "" : String(value);
};

const resolvePresignedPostPayload = (data) => {
  if (!data || typeof data !== "object") return null;
  const nested =
    data.presigned_post ||
    data.presignedPost ||
    data.upload ||
    data.post ||
    data;
  if (!nested || typeof nested !== "object") return null;
  return nested;
};

const resolveUploadedFilePath = (presignedData, fallbackFilename) => {
  const fields =
    presignedData?.fields && typeof presignedData.fields === "object"
      ? presignedData.fields
      : {};
  return (
    fields.key ||
    fields.Key ||
    presignedData?.file_path ||
    presignedData?.filePath ||
    presignedData?.filename ||
    presignedData?.key ||
    fallbackFilename ||
    ""
  );
};

/**
 * Upload the selected file to S3 using the presigned POST fields.
 * File field must be appended last (S3 requirement). Uses fetch to the S3 URL.
 */
export const uploadFileToHrPresignedPost = async (presignedData, file) => {
  const url = String(presignedData?.url || "").trim();
  const fields = presignedData?.fields;
  if (!url || !fields || typeof fields !== "object") {
    throw new Error(
      "Invalid upload configuration received from server. Please try again.",
    );
  }

  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", file);

  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      body: formData,
    });
  } catch (error) {
    throw new Error(
      "Failed to upload file to storage. Please check your connection and try again.",
    );
  }

  if (!response.ok) {
    const responseText = await response.text().catch(() => "");
    const messageMatch = String(responseText).match(
      /<Message>([^<]+)<\/Message>/i,
    );
    throw new Error(
      messageMatch?.[1] ||
        "Failed to upload file to storage. Please try again.",
    );
  }

  return true;
};

const flagKeyFor = (sectionKey, fieldKey, recordIndex = null) =>
  recordIndex == null
    ? `${sectionKey}-${fieldKey}`
    : `${sectionKey}-${recordIndex}-${fieldKey}`;

/**
 * Category A — which Review Request fields HR may edit directly.
 * Keys are the existing Jedi-HR UI field keys.
 * Bank, declarations, and identity/verified fields are intentionally absent.
 */
const HR_EDITABLE_FIELDS = {
  personal: new Set([
    "alternative_phone_number",
    "email_address",
    "marital_status",
  ]),
  address: new Set([
    "permanent_address_house",
    "permanent_address_street",
    "permanent_address_area",
    "permanent_address_landmark",
    "permanent_address_city",
    "permanent_address_state",
    "permanent_address_pincode",
    "current_address_house",
    "current_address_street",
    "current_address_area",
    "current_address_landmark",
    "current_address_city",
    "current_address_state",
    "current_address_pincode",
  ]),
  education: new Set([
    "qualification",
    "degree",
    "start_date",
    "graduation_date",
    "university",
    "college",
    "cgpa_percentage",
    "maximum_cgpa_percentage",
  ]),
  employment: new Set(["company", "designation", "start_date", "last_date"]),
  insurance: new Set([
    "blood_group",
    "nominee_full_name",
    "nominee_relationship",
    "nominee_dob",
    "emergency_relationship",
    "emergency_full_name",
    "emergency_phone",
    "emergency_email",
  ]),
};

const HR_REPLACEABLE_DOCUMENTS = {
  education: /^doc-edu-(?:marksheet|certificate)-\d+$/,
  employment: new Set([
    "doc-resignation",
    "doc-relieving",
    "doc-epfo",
    "doc-form2",
    "doc-form11",
    "doc-pf-authorization",
    "doc-formf",
  ]),
  insurance: new Set(["doc-nischint"]),
};

/**
 * Existing queue / approval_status values where HR may direct-edit.
 * These are aliases already used by Review Queue and approval details.
 * Terminal statuses stay locked. No new status strings are written.
 */
const HR_EDITABLE_STATUSES = new Set([
  "pending",
  "pending_review",
  "submitted",
  "in_progress",
  "inprogress",
  "awaiting",
  "awaiting_resubmission",
  "flagged",
  "resubmitted",
  "re_submitted",
]);

const HR_LOCKED_STATUSES = new Set([
  "completed",
  "complete",
  "approved",
  "activated",
  "rejected",
  "withdrawn",
]);

const DATE_FIELDS = new Set([
  "start_date",
  "graduation_date",
  "last_date",
  "nominee_dob",
]);

const EMAIL_FIELDS = new Set(["email_address", "emergency_email"]);
const PHONE_FIELDS = new Set(["alternative_phone_number", "emergency_phone"]);
const PINCODE_FIELDS = new Set([
  "current_address_pincode",
  "permanent_address_pincode",
]);

export const HR_PINCODE_DEPENDENTS = {
  current_address_pincode: {
    city: "current_address_city",
    state: "current_address_state",
  },
  permanent_address_pincode: {
    city: "permanent_address_city",
    state: "permanent_address_state",
  },
};

const EDUCATION_MASTER_CATEGORIES = {
  board: "board",
  university: "university",
  college: "college",
  diploma: "diploma",
  affiliating_body: "affiliating_body",
  graduate: "graduate",
  post_graduate: "post_graduate",
  doctorate: "doctorate",
};

const COLLEGE_MASTER_CATEGORIES = [
  EDUCATION_MASTER_CATEGORIES.university,
  EDUCATION_MASTER_CATEGORIES.college,
];
const CGPA_FIELDS = new Set(["cgpa_percentage", "maximum_cgpa_percentage"]);

const EMAIL_REGEX = sectionWiseFields.contact.personal_email_id.regex;
const PHONE_REGEX = sectionWiseFields.contact.personal_mobile_no.regex;
const PINCODE_REGEX = sectionWiseFields.address.pincode.regex;
const GPA_REGEX = sectionWiseFields.education.gpa.regex;

export const HR_DOCUMENT_ACCEPT = ".jpeg,.png,.pdf,.jpg";
export const HR_DOCUMENT_MAX_BYTES = 5 * 1048576;
export const HR_DOCUMENT_MAX_SIZE_MESSAGE = "Maximum file size is 5 MB";
export const HR_EDIT_UNAVAILABLE_MESSAGE =
  "This edit can’t be saved yet. The onboarding service does not provide an HR Direct Edit action.";
export const HR_DOCUMENT_UNAVAILABLE_MESSAGE =
  "This document can’t be replaced yet. The onboarding service does not provide a document replace action.";
export const HR_CLASSIFIER_UNAVAILABLE_MESSAGE =
  "Document type could not be checked. The replacement was not accepted.";
export const HR_CONFLICT_MESSAGE =
  "This request has been updated. Reload to see the latest before editing.";
export const HR_NO_CHANGE_MESSAGE = "No change to save";
export const HR_REASON_REQUIRED_MESSAGE = "Reason for edit is required.";

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export const canHrDirectEditRequest = ({ approvalStatus, queueStatus } = {}) => {
  const approval = normalizeStatus(approvalStatus);
  const queue = normalizeStatus(queueStatus);
  if (HR_LOCKED_STATUSES.has(approval) || HR_LOCKED_STATUSES.has(queue)) {
    return false;
  }
  return HR_EDITABLE_STATUSES.has(approval) || HR_EDITABLE_STATUSES.has(queue);
};

/**
 * Terminal / locked approval statuses where Save as Draft, Flag, and Create ID
 * stay unavailable. HR-editable statuses (incl. flagged / submitted) are not
 * treated as action-locked — HR Direct Edit must not block those buttons.
 */
export const isHrApprovalActionLocked = (approvalStatus) =>
  HR_LOCKED_STATUSES.has(normalizeStatus(approvalStatus));

export const isHrEditableField = (sectionKey, fieldKey) =>
  !!HR_EDITABLE_FIELDS[sectionKey]?.has(fieldKey);

export const isHrReplaceableDocument = (sectionKey, docId) => {
  const rule = HR_REPLACEABLE_DOCUMENTS[sectionKey];
  if (!rule || !docId) return false;
  if (rule instanceof RegExp) return rule.test(String(docId));
  return rule.has(String(docId));
};

export const getHrEditControl = (fieldKey) => {
  if (fieldKey === "marital_status") return "marital";
  if (fieldKey === "blood_group") return "blood";
  if (fieldKey === "qualification") return "qualification";
  if (DATE_FIELDS.has(fieldKey)) return "date";
  return "text";
};

export const normalizePincode = (value) =>
  String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 6);

export const isCompletePincode = (value) => normalizePincode(value).length === 6;

/**
 * Same category and input rules as Candidate Onboarding education master.
 * Dropdowns load the category list. Search fields query after 3 characters.
 */
export const getHrEducationMasterField = (qualification, fieldKey) => {
  const level = normalizeEducationQualification(qualification);
  if (
    !["10th", "12th", "diploma", "graduate", "post_graduate", "doctorate"].includes(
      level,
    )
  ) {
    return null;
  }

  if (fieldKey === "degree") {
    if (level === "10th" || level === "12th") return null;
    const category =
      level === "diploma"
        ? EDUCATION_MASTER_CATEGORIES.diploma
        : EDUCATION_MASTER_CATEGORIES[level];
    return category
      ? { mode: "dropdown", categories: [category], minSearchChars: 0, allowAdd: false }
      : null;
  }

  if (fieldKey === "university") {
    if (level === "10th" || level === "12th") {
      return {
        mode: "dropdown",
        categories: [EDUCATION_MASTER_CATEGORIES.board],
        minSearchChars: 0,
        allowAdd: false,
      };
    }
    if (level === "diploma") {
      return {
        mode: "dropdown",
        categories: [EDUCATION_MASTER_CATEGORIES.affiliating_body],
        minSearchChars: 0,
        allowAdd: false,
      };
    }
    return {
      mode: "search",
      categories: [EDUCATION_MASTER_CATEGORIES.university],
      minSearchChars: 3,
      allowAdd: true,
    };
  }

  if (fieldKey === "college") {
    return {
      mode: "search",
      categories: COLLEGE_MASTER_CATEGORIES,
      minSearchChars: 3,
      allowAdd: true,
    };
  }

  return null;
};

export const hrRelatedFieldLabel = (fieldKey, qualification) => {
  if (
    fieldKey === "current_address_city" ||
    fieldKey === "permanent_address_city"
  ) {
    return "City/Town/Village";
  }
  if (
    fieldKey === "current_address_state" ||
    fieldKey === "permanent_address_state"
  ) {
    return "State";
  }
  if (fieldKey === "degree") {
    return getEducationDegreeLabel(qualification) || "Degree";
  }
  return "";
};

export const getHrEditOptions = (fieldKey) => {
  if (fieldKey === "marital_status") return maritalStatus;
  if (fieldKey === "blood_group") return bloodGroups;
  if (fieldKey === "qualification") return EDUCATION_QUALIFICATION_LEVELS;
  return [];
};

export const readSectionValue = (formData, sectionKey, fieldKey, recordIndex) => {
  if (sectionKey === "education" || sectionKey === "employment") {
    if (recordIndex != null) {
      return formData?.[sectionKey]?.records?.[recordIndex]?.[fieldKey] ?? "";
    }
    return formData?.[sectionKey]?.[fieldKey] ?? "";
  }
  return formData?.[sectionKey]?.[fieldKey] ?? "";
};

const readSectionRecord = (formData, sectionKey, recordIndex) => {
  if (
    (sectionKey === "education" || sectionKey === "employment") &&
    recordIndex != null
  ) {
    return formData?.[sectionKey]?.records?.[recordIndex] || {};
  }
  return formData?.[sectionKey] || {};
};

export const normalizePhoneDigits = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
  return digits;
};

const parseEditDate = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date || moment.isMoment(value)) {
    const parsed = moment(value);
    return parsed.isValid() ? parsed : null;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const strict = moment(
    raw,
    ["YYYY-MM-DD", "DD-MM-YYYY", "DD MMM YYYY", "DD/MM/YYYY", moment.ISO_8601],
    true,
  );
  if (strict.isValid()) return strict;
  const loose = moment(raw);
  return loose.isValid() ? loose : null;
};

const normalizeComparable = (fieldKey, value) => {
  if (PHONE_FIELDS.has(fieldKey)) return normalizePhoneDigits(value);
  if (EMAIL_FIELDS.has(fieldKey)) return String(value ?? "").trim().toLowerCase();
  if (DATE_FIELDS.has(fieldKey)) {
    const parsed = parseEditDate(value);
    return parsed ? parsed.format("YYYY-MM-DD") : String(value ?? "").trim();
  }
  if (CGPA_FIELDS.has(fieldKey)) return String(value ?? "").trim();
  return String(value ?? "").trim();
};

export const valuesAreSame = (fieldKey, left, right) =>
  normalizeComparable(fieldKey, left) === normalizeComparable(fieldKey, right);

const requiredMessage = (label) => `Enter ${String(label || "a value").toLowerCase()}`;

const validateTextRules = (fieldKey, label, value, isRequired) => {
  const text = String(value ?? "").trim();
  if (!text) {
    return isRequired ? requiredMessage(label) : "";
  }
  if (EMAIL_FIELDS.has(fieldKey) && !EMAIL_REGEX.test(text)) {
    return "Enter a valid email address";
  }
  if (PHONE_FIELDS.has(fieldKey) && !PHONE_REGEX.test(normalizePhoneDigits(text))) {
    return "Enter a valid 10-digit mobile number";
  }
  if (PINCODE_FIELDS.has(fieldKey) && !PINCODE_REGEX.test(text)) {
    return "Enter a valid 6-digit pincode";
  }
  if (CGPA_FIELDS.has(fieldKey) && !GPA_REGEX.test(text)) {
    return `Enter a valid ${label || "value"}`;
  }
  return "";
};

const validateDateValue = (label, value, isRequired) => {
  const text = String(value ?? "").trim();
  if (!text) return isRequired ? requiredMessage(label) : "";
  if (!parseEditDate(text)) return `Enter a valid ${label || "date"}`;
  return "";
};

/**
 * Validate one HR edit, including sibling dates / CGPA and a qualification's degree.
 * Returns an error string, or "" when the draft can be saved.
 */
export const validateHrFieldEdit = ({
  sectionKey,
  fieldKey,
  label,
  isRequired = false,
  values = {},
  savedRecord = {},
  reason = "",
}) => {
  const reasonText = String(reason ?? "").trim();
  if (!reasonText) return HR_REASON_REQUIRED_MESSAGE;

  const changedKeys = Object.keys(values).filter(
    (key) => !valuesAreSame(key, savedRecord[key], values[key]),
  );
  if (changedKeys.length === 0) return HR_NO_CHANGE_MESSAGE;

  const draft = values[fieldKey];
  let fieldError = "";
  if (DATE_FIELDS.has(fieldKey)) {
    fieldError = validateDateValue(label, draft, isRequired);
  } else if (fieldKey === "qualification") {
    fieldError = String(draft ?? "").trim() ? "" : requiredMessage(label);
  } else if (fieldKey === "marital_status" || fieldKey === "blood_group") {
    const text = String(draft ?? "").trim();
    if (!text && isRequired) fieldError = requiredMessage(label);
    else if (text) {
      const options = getHrEditOptions(fieldKey);
      const allowed =
        fieldKey === "qualification"
          ? options.map((item) => item.key)
          : options;
      if (allowed.length && !allowed.includes(text)) {
        fieldError = `Select a valid ${label || "value"}`;
      }
    }
  } else {
    fieldError = validateTextRules(fieldKey, label, draft, isRequired);
  }
  if (fieldError) return fieldError;

  const merged = { ...savedRecord, ...values };

  if (fieldKey === "qualification") {
    const degreeLabel = getEducationDegreeLabel(merged.qualification);
    if (degreeLabel && !String(merged.degree ?? "").trim()) {
      return `Enter ${degreeLabel.toLowerCase()}`;
    }
  }

  if (sectionKey === "education" && (fieldKey === "start_date" || fieldKey === "graduation_date" || fieldKey === "qualification")) {
    const start = parseEditDate(merged.start_date);
    const end = parseEditDate(merged.graduation_date);
    if (start && end && !end.isAfter(start, "day")) {
      return "Graduation Date must be after Start Date";
    }
  }

  if (sectionKey === "employment" && (fieldKey === "start_date" || fieldKey === "last_date")) {
    const start = parseEditDate(merged.start_date);
    const end = parseEditDate(merged.last_date);
    if (start && end && !end.isAfter(start, "day")) {
      return "Last Date must be after Start Date";
    }
  }

  if (CGPA_FIELDS.has(fieldKey) || fieldKey === "qualification") {
    const scoreText = String(merged.cgpa_percentage ?? "").trim();
    const maxText = String(merged.maximum_cgpa_percentage ?? "").trim();
    if (scoreText && maxText && GPA_REGEX.test(scoreText) && GPA_REGEX.test(maxText)) {
      const score = Number(scoreText);
      const max = Number(maxText);
      if (Number.isFinite(score) && Number.isFinite(max) && score > max) {
        return "CGPA/Percentage must not exceed Maximum CGPA/Percentage";
      }
    }
  }

  return "";
};

export const sanitizeHrFieldValue = (fieldKey, value) => {
  if (PHONE_FIELDS.has(fieldKey)) return normalizePhoneDigits(value);
  if (DATE_FIELDS.has(fieldKey)) {
    const parsed = parseEditDate(value);
    return parsed ? parsed.format("YYYY-MM-DD") : String(value ?? "").trim();
  }
  if (EMAIL_FIELDS.has(fieldKey)) return String(value ?? "").trim();
  return typeof value === "string" ? value.trim() : value ?? "";
};

const recordIndexesMatch = (left, right) => {
  const leftEmpty = left == null || left === "";
  const rightEmpty = right == null || right === "";
  if (leftEmpty && rightEmpty) return true;
  if (leftEmpty || rightEmpty) return false;
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isNaN(leftNumber) || Number.isNaN(rightNumber)) {
    return String(left) === String(right);
  }
  return leftNumber === rightNumber;
};

const educationDocumentRecordIndex = (fieldKey) => {
  const match = String(fieldKey || "").match(
    /^doc-edu-(?:marksheet|certificate)-(\d+)$/,
  );
  return match ? Number(match[1]) : null;
};

/**
 * Drop only the open flag for this field.
 * Repeating education and employment rows match field key plus record_index.
 * A matching field_name on another record is left in place.
 */
export const clearFlagForHrEdit = (
  fieldFlags = {},
  { sectionKey, fieldKey, recordIndex = null } = {},
) => {
  const documentRecordIndex = educationDocumentRecordIndex(fieldKey);
  const targetRecordIndex =
    documentRecordIndex != null ? documentRecordIndex : recordIndex;
  const next = { ...fieldFlags };
  let removed = false;

  Object.entries(fieldFlags || {}).forEach(([key, flag]) => {
    if (!flag || flag.sectionKey !== sectionKey || flag.fieldKey !== fieldKey) {
      return;
    }
    const flagRecordIndex =
      documentRecordIndex != null ? documentRecordIndex : flag.recordIndex;
    if (!recordIndexesMatch(flagRecordIndex, targetRecordIndex)) return;
    delete next[key];
    removed = true;
  });

  if (!removed) {
    const directKey = flagKeyFor(
      sectionKey,
      fieldKey,
      documentRecordIndex != null ? null : recordIndex,
    );
    if (next[directKey]) {
      delete next[directKey];
      removed = true;
    }
  }

  return removed ? next : fieldFlags;
};

export const validateHrEditReason = (reason) => {
  const reasonText = String(reason ?? "").trim();
  return reasonText ? "" : HR_REASON_REQUIRED_MESSAGE;
};

export const appendHrEditHistory = (history = [], entries = []) => {
  const next = Array.isArray(history) ? [...history] : [];
  entries.forEach((entry) => {
    if (!entry) return;
    next.push(entry);
  });
  return next.sort((a, b) => {
    const aTime = Date.parse(a?.editedAt || "") || 0;
    const bTime = Date.parse(b?.editedAt || "") || 0;
    return aTime - bTime;
  });
};

export const countEditedFields = (history = []) => {
  const keys = new Set();
  history.forEach((entry) => {
    if (!entry) return;
    const record =
      entry.recordIndex == null || entry.recordIndex === ""
        ? ""
        : `-${entry.recordIndex}`;
    keys.add(`${entry.sectionKey}${record}-${entry.fieldKey}`);
  });
  return keys.size;
};

export const historyForField = (
  history = [],
  sectionKey,
  fieldKey,
  recordIndex = null,
) =>
  (history || []).filter((entry) => {
    if (entry?.sectionKey !== sectionKey || entry?.fieldKey !== fieldKey) {
      return false;
    }
    const entryIndex = entry.recordIndex == null ? null : entry.recordIndex;
    const targetIndex = recordIndex == null ? null : recordIndex;
    return entryIndex === targetIndex;
  });

export const readSavedRecord = (formData, sectionKey, recordIndex) =>
  readSectionRecord(formData, sectionKey, recordIndex);

export const validateReplacementFile = (file) => {
  if (!file) return "Select a document to replace";
  const extension = String(file.name || "")
    .split(".")
    .pop()
    .toLowerCase();
  if (!["jpeg", "jpg", "png", "pdf"].includes(extension)) {
    return "Only JPEG, PNG, and PDF files are allowed";
  }
  if (file.size > HR_DOCUMENT_MAX_BYTES) {
    return HR_DOCUMENT_MAX_SIZE_MESSAGE;
  }
  return "";
};

/**
 * Document-type classifier hook for HR replacement.
 * No classifier endpoint exists in this app — pass through so the
 * create_presigned_post → upload → hr_replace_document flow can run.
 * Legibility / blur / tamper AI checks are not part of this path.
 */
export const classifyHrReplacementDocument = async () => ({
  ok: true,
  code: "CLASSIFIER_SKIPPED",
});

const pendingEditIdentity = (entry = {}) => {
  const sectionKey = String(entry.sectionKey ?? entry.section_key ?? "");
  const fieldKey = String(entry.fieldKey ?? entry.field_key ?? "");
  // Education and employment repeat field_key across records. Identity
  // includes record_index so one record cannot replace another.
  // Personal, address, and insurance stay section + field only.
  const usesRecordIndex =
    sectionKey === "education" || sectionKey === "employment";
  if (!usesRecordIndex) {
    return `${sectionKey}|${fieldKey}`;
  }
  const recordIndex =
    entry.recordIndex == null && entry.record_index == null
      ? ""
      : String(entry.recordIndex ?? entry.record_index);
  return `${sectionKey}|${fieldKey}|${recordIndex}`;
};

/**
 * Keep one pending save_progress edit per section/field/record.
 * A later confirmed edit of the same field replaces the earlier pending value.
 */
export const upsertPendingHrEdits = (pendingEdits = [], entries = []) => {
  const next = new Map();
  (Array.isArray(pendingEdits) ? pendingEdits : []).forEach((entry) => {
    if (!entry) return;
    next.set(pendingEditIdentity(entry), entry);
  });
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    if (!entry) return;
    const fieldKey = String(entry.fieldKey ?? entry.field_key ?? "").trim();
    const reason = String(entry.reason ?? "").trim();
    if (!fieldKey || !reason) return;
    const newValue =
      entry.newValue !== undefined
        ? entry.newValue
        : entry.new_value !== undefined
          ? entry.new_value
          : "";
    const normalized = {
      sectionKey: entry.sectionKey ?? entry.section_key ?? "",
      fieldKey,
      recordIndex:
        entry.recordIndex != null
          ? entry.recordIndex
          : entry.record_index != null
            ? entry.record_index
            : null,
      field_key: fieldKey,
      new_value: newValue == null ? "" : newValue,
      reason,
    };
    next.set(pendingEditIdentity(normalized), normalized);
  });
  return Array.from(next.values());
};

/**
 * Field-level HR Direct Edit confirms locally.
 * Persistence happens later through save_progress.edits with request version.
 */
export const persistHrDirectEdit = async () => ({
  ok: true,
  code: "LOCAL_PENDING",
});

/**
 * Persist HR document replacement via Redux thunks:
 * create_presigned_post → S3 upload → hr_replace_document
 */
export const persistHrDocumentReplace = async (params = {}) => {
  try {
    const { dispatch } = params;
    if (typeof dispatch !== "function") {
      return {
        ok: false,
        code: "REPLACE_FAILED",
        message: "Document replacement failed",
      };
    }

    const documentType =
      params.documentType ||
      resolveHrReplaceDocumentType(params.fieldKey || params.docId);
    if (!documentType) {
      return {
        ok: false,
        code: "UNKNOWN_DOCUMENT_TYPE",
        message: "Unknown document type for replacement",
      };
    }

    const file = params.file;
    const localFilename = String(file?.name || "").trim();
    if (!file || !localFilename) {
      return {
        ok: false,
        code: "REPLACE_FAILED",
        message: "Select a document to replace",
      };
    }

    const presignResponse = await dispatch(
      createPresignedPost({
        onboarding_id: params.onboardingId,
        doc_ref_id: params.docRefId ?? readDocRefId(params.doc),
        firstname: String(params.firstName || "").trim(),
        lastname: String(params.lastName || "").trim(),
        document_type: documentType,
        filename: localFilename,
        upload_for: "HR",
      }),
    ).unwrap();

    const presigned = resolvePresignedPostPayload(
      extractOnboardingActionData(presignResponse),
    );
    const uploadUrl = String(presigned?.url || "").trim();
    const fields = presigned?.fields;
    if (!uploadUrl || !fields || typeof fields !== "object") {
      return {
        ok: false,
        code: "REPLACE_FAILED",
        message:
          "Invalid upload configuration received from server. Please try again.",
      };
    }

    const storageKey = String(
      resolveUploadedFilePath(presigned, localFilename) || "",
    ).trim();

    await uploadFileToHrPresignedPost(
      { ...presigned, url: uploadUrl, fields },
      file,
    );

    if (!storageKey) {
      return {
        ok: false,
        code: "REPLACE_FAILED",
        message:
          "Upload succeeded but storage path was not returned. Please try again.",
      };
    }

    const sectionKey = String(params.sectionKey ?? params.section_key ?? "")
      .trim()
      .toLowerCase();
    const recordIndex = resolveHrDocumentRecordIndex(params);

    const replaceResponse = await dispatch(
      hrReplaceDocument({
        onboarding_id: params.onboardingId,
        version: params.version,
        document_type: documentType,
        filename: storageKey,
        section_key: sectionKey,
        record_index: recordIndex,
      }),
    ).unwrap();

    const data = extractOnboardingActionData(replaceResponse);
    let nextVersion = null;
    if (data && typeof data === "object") {
      nextVersion = extractRequestVersion(data);
      if (nextVersion == null) {
        nextVersion = extractRequestVersion({
          raw: data,
          body: { data },
        });
      }
    }
    if (nextVersion == null) {
      nextVersion = extractRequestVersion(replaceResponse);
    }

    return {
      ok: true,
      storageKey,
      localFilename,
      version: nextVersion,
      data,
      approval_status: data?.approval_status,
    };
  } catch (error) {
    return {
      ok: false,
      code: "REPLACE_FAILED",
      message: rejectMessageFromThunk(error, "Document replacement failed"),
    };
  }
};
